/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/planGate.ts
 *
 * Eine Sperre ist eine Verkaufschance.
 *
 * Ein 402 trifft den Kunden im Moment der höchsten Kaufbereitschaft — er wollte
 * die Funktion gerade. „Dieses Feature ist Teil eines höheren Plans" verschenkt
 * diesen Moment vollständig. Die Sperrkarte beantwortet stattdessen die drei
 * Fragen, die ein Käufer wirklich hat: Was bekomme ich, welche Stufe schaltet
 * es frei, was kostet sie.
 *
 * Dieses Modul liefert das **Rechnen** dazu, nicht das Aussehen: welche Stufe
 * die günstigste ist, die das Fehlende trägt, und was sie kostet. Die Karte
 * selbst baut jede App in ihrem eigenen Stil — ein gemeinsames JSX hätte sich
 * an sechs Designsysteme gleichzeitig binden müssen.
 *
 * Warum die Stufe nicht hartkodiert wird: bei einer Umpreisung oder einem
 * Tier-Umbau steht sonst in der Karte ein Name, den es nicht mehr gibt. Genau
 * so ist in 121surveys „Pro buchen" auf jeder Kachel stehengeblieben, nachdem
 * die Stufe stillgelegt war.
 */

/**
 * Was dieses Modul von einer Stufe braucht — bewusst das Minimum.
 *
 * Kein `slug` und keine `id`: die liest hier nichts, und beide heissen in den
 * Payloads der Flotte nicht gleich (`FleetPlan` traegt den Slug historisch als
 * `id`, `slug` ist optional). Eine Pflichtangabe, die niemand benutzt, macht
 * den Baustein nur fuer die Haelfte der Apps typkompatibel.
 */
export interface GateFaehigePlan {
  name: string
  price_monthly_cents: number
  price_annual_cents?: number | null
  is_public?: boolean
}

/**
 * React-Query-Optionen für jede Abfrage, die in einer Sperre enden kann.
 *
 * **Als Konstante und nicht als Kommentar**, weil dieselbe Falle in 121suite
 * zweimal unabhängig neu entdeckt wurde: mit dem Standard-Retry meldet React
 * Query während des Wiederholungsversuchs weder `isLoading` noch `error` noch
 * `data`. Die Seite rendert dann Überschrift und darunter nichts — sie sieht
 * kaputt aus statt gesperrt.
 */
export const PLAN_GATE_QUERY_OPTIONS = {
  retry: 0,
  throwOnError: false,
} as const

/**
 * Die günstigste Stufe, die `traegt` erfüllt.
 *
 * `traegt` bekommt eine Stufe und sagt, ob sie das Fehlende hätte — welche
 * Achse das ist, weiss nur die App (ein Modul-Flag, ein Sitzplatz-Limit, ein
 * Volumen). Nicht-öffentliche Stufen fallen raus: eine Karte, die auf einen
 * nicht buchbaren Tarif zeigt, ist eine Sackgasse.
 *
 * Gibt `null` zurück, wenn keine Stufe passt. Dann zeigt die Karte Nutzen und
 * einen Kontakt-CTA, aber keinen Preis — statt einen zu erfinden.
 */
export function guenstigsteStufeMit<T extends GateFaehigePlan>(
  plans: readonly T[],
  traegt: (plan: T) => boolean,
): T | null {
  const kandidaten = plans
    .filter((p) => p.is_public !== false)
    .filter((p) => p.price_monthly_cents > 0)
    .filter(traegt)
    .sort((a, b) => a.price_monthly_cents - b.price_monthly_cents)
  return kandidaten[0] ?? null
}

/**
 * Monatlicher Anzeigepreis einer Stufe in Cent.
 *
 * Bei Jahreszahlung der auf den Monat heruntergerechnete Betrag — das ist die
 * Zahl, die neben „pro Monat" steht. Fehlt der Jahrespreis, bleibt es beim
 * Monatspreis; ein stiller Rückfall auf eine andere Währung oder einen
 * geschätzten Betrag findet nicht statt (Regel #7).
 */
export function anzeigePreisCents(
  plan: GateFaehigePlan,
  abrechnung: 'monthly' | 'annual',
): number {
  if (abrechnung === 'annual' && plan.price_annual_cents) {
    return Math.round(plan.price_annual_cents / 12)
  }
  return plan.price_monthly_cents
}

/** Was eine Sperrkarte anzeigt. `plan === null` heisst: kein Preis nennbar. */
export interface Sperrkarte<T extends GateFaehigePlan> {
  /** Der Maschinencode aus dem 402 — für Telemetrie, nie zum Anzeigen. */
  code: string | null
  /** Die günstigste Stufe, die es freischaltet, oder `null`. */
  plan: T | null
  /** Monatspreis dieser Stufe in Cent, oder `null`. */
  preisCents: number | null
}

/**
 * Alles zusammenrechnen, was die Karte braucht.
 *
 * Bewusst tolerant: fehlt der Katalog (Abruf gescheitert), kommt trotzdem eine
 * Karte heraus — nur ohne Preis. Die Sperrkarte darf nie verschwinden, weil ein
 * Nebenabruf schiefging; Text und CTA funktionieren auch ohne Betrag.
 */
export function sperrkarte<T extends GateFaehigePlan>(args: {
  code: string | null
  plans: readonly T[] | undefined
  traegt: (plan: T) => boolean
  abrechnung?: 'monthly' | 'annual'
}): Sperrkarte<T> {
  const plan = args.plans ? guenstigsteStufeMit(args.plans, args.traegt) : null
  return {
    code: args.code,
    plan,
    preisCents: plan ? anzeigePreisCents(plan, args.abrechnung ?? 'monthly') : null,
  }
}
